import { DEFAULT_TIMEZONE, type CountryCode } from './countries.ts';
import type { CalendarPref, DigitPref } from './format.ts';
import type { Locale } from './locales.ts';

/**
 * Time display policy (spec 11A). Times are stored in UTC and always shown in the *effective* zone: the user's
 * override when set, otherwise the tenant default. UTC appears only when it is the selected zone.
 */
export type TimeFormat = '12h' | '24h';
export type DateStyle = 'short' | 'medium' | 'long';

export interface DisplayDefaults {
  timezone: string;
  timeFormat: TimeFormat;
  dateStyle: DateStyle;
  calendar: CalendarPref;
  numerals: DigitPref;
  weekStart: number;
  locale: Locale;
}

/** User-level overrides; null/undefined = inherit the tenant default. */
export type DisplayOverrides = { [K in keyof DisplayDefaults]?: DisplayDefaults[K] | null };

export interface EffectiveDisplay extends DisplayDefaults {
  /** Which fields come from the user rather than the tenant (for "Use organisation default" UI). */
  overridden: (keyof DisplayDefaults)[];
}

export function resolveDisplay(tenant: DisplayDefaults, user: DisplayOverrides = {}): EffectiveDisplay {
  const out = { ...tenant, overridden: [] as (keyof DisplayDefaults)[] };
  for (const key of Object.keys(tenant) as (keyof DisplayDefaults)[]) {
    const v = user[key];
    if (v !== null && v !== undefined) {
      (out as Record<string, unknown>)[key] = v;
      out.overridden.push(key);
    }
  }
  if (!isValidTimeZone(out.timezone)) out.timezone = tenant.timezone;
  return out;
}

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Sensible tenant defaults per country, used by the onboarding wizard. */
export const COUNTRY_DEFAULTS: Record<
  CountryCode,
  {
    region: string;
    currency: string;
    timezone: string;
    weekStart: number;
    locale: Locale;
    timeFormat: TimeFormat;
  }
> = {
  AE: {
    region: 'ae',
    currency: 'AED',
    timezone: DEFAULT_TIMEZONE.AE,
    weekStart: 1,
    locale: 'en',
    timeFormat: '12h',
  },
  SA: {
    region: 'sa',
    currency: 'SAR',
    timezone: DEFAULT_TIMEZONE.SA,
    weekStart: 0,
    locale: 'ar',
    timeFormat: '12h',
  },
  QA: {
    region: 'qa',
    currency: 'QAR',
    timezone: DEFAULT_TIMEZONE.QA,
    weekStart: 0,
    locale: 'ar',
    timeFormat: '12h',
  },
  KW: {
    region: 'kw',
    currency: 'KWD',
    timezone: DEFAULT_TIMEZONE.KW,
    weekStart: 0,
    locale: 'ar',
    timeFormat: '12h',
  },
  BH: {
    region: 'bh',
    currency: 'BHD',
    timezone: DEFAULT_TIMEZONE.BH,
    weekStart: 0,
    locale: 'ar',
    timeFormat: '12h',
  },
  OM: {
    region: 'om',
    currency: 'OMR',
    timezone: DEFAULT_TIMEZONE.OM,
    weekStart: 0,
    locale: 'ar',
    timeFormat: '12h',
  },
  IN: {
    region: 'in',
    currency: 'INR',
    timezone: DEFAULT_TIMEZONE.IN,
    weekStart: 1,
    locale: 'en',
    timeFormat: '12h',
  },
};

/** Zones offered in pickers: GCC and India first, then UTC, then every other IANA zone. */
export function timeZoneOptions(): string[] {
  const pinned = [
    'Asia/Dubai',
    'Asia/Riyadh',
    'Asia/Qatar',
    'Asia/Kuwait',
    'Asia/Bahrain',
    'Asia/Muscat',
    'Asia/Kolkata',
    'UTC',
  ];
  const all = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  return [...pinned, ...all.filter((z) => !pinned.includes(z))];
}

/** "GMT+4"-style offset label for a zone at a given instant. */
export function zoneLabel(tz: string, at = new Date(), locale = 'en'): string {
  const part = new Intl.DateTimeFormat(locale, { timeZone: tz, timeZoneName: 'shortOffset' })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? tz;
}

/** Offset of a zone from UTC at an instant, in minutes (e.g. Asia/Dubai → 240). */
export function zoneOffsetMinutes(tz: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return Math.round((asUtc - at.getTime()) / 60_000);
}

/**
 * Converts a wall-clock value typed by the user ("2026-10-12T09:30", as from <input type="datetime-local">) in the
 * user's effective zone to a UTC ISO instant. Browsers would otherwise interpret it in the device's zone.
 */
export function zonedWallTimeToIso(value: string, tz: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  const naive = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!);
  let t = naive - zoneOffsetMinutes(tz, new Date(naive)) * 60_000;
  t = naive - zoneOffsetMinutes(tz, new Date(t)) * 60_000; // second pass settles DST edges
  return new Date(t).toISOString();
}

/** The reverse: a UTC instant as "YYYY-MM-DDTHH:mm" wall time in a zone, for pre-filling inputs. */
export function isoToZonedWallTime(iso: string, tz: string): string {
  const d = new Date(iso);
  const local = new Date(d.getTime() + zoneOffsetMinutes(tz, d) * 60_000);
  return local.toISOString().slice(0, 16);
}
